import { useEffect, useRef, useState } from "react";

const defaultDialog = {
  open: false,
  mode: "alert",
  type: "info",
  title: "",
  message: "",
  confirmText: "Tamam",
  cancelText: "Vazgeç",
  inputValue: "",
  inputPlaceholder: "",
  multiline: false
};

const getDialogTitle = (type) => {
  if (type === "success") return "İşlem tamamlandı";
  if (type === "danger") return "Dikkat";
  if (type === "warning") return "Uyarı";
  if (type === "error") return "Hata";
  return "Bilgilendirme";
};

const getDialogPalette = (type) => {
  if (type === "success") {
    return { icon: "✓", background: "#dcfce7", color: "#166534", button: "#16a34a" };
  }

  if (type === "danger" || type === "error") {
    return { icon: "!", background: "#fee2e2", color: "#b91c1c", button: "#dc2626" };
  }

  if (type === "warning") {
    return { icon: "!", background: "#fef3c7", color: "#92400e", button: "#f59e0b" };
  }

  return { icon: "i", background: "#dbeafe", color: "#1d4ed8", button: "#124254" };
};

function AppDialogProvider() {
  const resolverRef = useRef(null);
  const nativeAlertRef = useRef(null);
  const nativeConfirmRef = useRef(null);
  const nativePromptRef = useRef(null);
  const [dialog, setDialog] = useState(defaultDialog);

  const showDialog = ({
    mode = "alert",
    type = "info",
    title,
    message,
    confirmText = mode === "alert" ? "Tamam" : "Onayla",
    cancelText = "Vazgeç",
    inputPlaceholder = "",
    defaultValue = "",
    multiline = false
  }) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setDialog({
      open: true,
      mode,
      type,
      title: title || getDialogTitle(type),
      message: String(message || ""),
      confirmText,
      cancelText,
      inputValue: String(defaultValue || ""),
      inputPlaceholder,
      multiline: Boolean(multiline)
    });
  });

  const closeDialog = (result) => {
    const resolver = resolverRef.current;
    const response = dialog.mode === "prompt"
      ? result ? dialog.inputValue : null
      : Boolean(result);

    resolverRef.current = null;
    setDialog((current) => ({ ...current, open: false }));

    if (resolver) resolver(response);
  };

  useEffect(() => {
    nativeAlertRef.current = window.alert;
    nativeConfirmRef.current = window.confirm;
    nativePromptRef.current = window.prompt;

    window.appAlert = (message, options = {}) => {
      showDialog({
        mode: "alert",
        type: options.type || "info",
        title: options.title,
        message,
        confirmText: options.confirmText || "Tamam"
      });
    };

    window.appConfirm = (message, options = {}) =>
      showDialog({
        mode: "confirm",
        type: options.type || "warning",
        title: options.title,
        message,
        confirmText: options.confirmText || "Onayla",
        cancelText: options.cancelText || "Vazgeç"
      });

    window.appPrompt = (message, options = {}) =>
      showDialog({
        mode: "prompt",
        type: options.type || "danger",
        title: options.title,
        message,
        confirmText: options.confirmText || "Onayla",
        cancelText: options.cancelText || "Vazgeç",
        inputPlaceholder: options.inputPlaceholder || "",
        defaultValue: options.defaultValue || "",
        multiline: options.multiline === true
      });

    window.alert = (message) => window.appAlert(message);

    return () => {
      if (nativeAlertRef.current) window.alert = nativeAlertRef.current;
      if (nativeConfirmRef.current) window.confirm = nativeConfirmRef.current;
      if (nativePromptRef.current) window.prompt = nativePromptRef.current;
      delete window.appAlert;
      delete window.appConfirm;
      delete window.appPrompt;
    };
  }, []);

  if (!dialog.open) return null;

  const palette = getDialogPalette(dialog.type);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.48)",
        zIndex: 30000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18
      }}
    >
      <div
        style={{
          width: 430,
          maxWidth: "94vw",
          background: "#fff",
          borderRadius: 22,
          boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
          padding: 22,
          border: "1px solid #e2e8f0"
        }}
      >
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 999,
              background: palette.background,
              color: palette.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 1000,
              fontSize: 20,
              flex: "0 0 42px"
            }}
          >
            {palette.icon}
          </div>

          <div style={{ flex: 1 }}>
            <h3 style={{ margin: "0 0 8px", color: "#124254", fontSize: 18 }}>
              {dialog.title}
            </h3>
            <div style={{ color: "#475569", fontWeight: 700, lineHeight: 1.5, whiteSpace: "pre-line" }}>
              {dialog.message}
            </div>
          </div>
        </div>

        {dialog.mode === "prompt" && dialog.multiline && (
          <textarea
            autoFocus
            rows={4}
            value={dialog.inputValue}
            onChange={(e) => setDialog((current) => ({ ...current, inputValue: e.target.value }))}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") closeDialog(true);
              if (e.key === "Escape") closeDialog(false);
            }}
            placeholder={dialog.inputPlaceholder}
            style={{
              width: "100%",
              marginTop: 18,
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid #cbd5e1",
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1.5,
              boxSizing: "border-box",
              resize: "vertical",
              minHeight: 110
            }}
          />
        )}

        {dialog.mode === "prompt" && !dialog.multiline && (
          <input
            autoFocus
            value={dialog.inputValue}
            onChange={(e) => setDialog((current) => ({ ...current, inputValue: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") closeDialog(true);
              if (e.key === "Escape") closeDialog(false);
            }}
            placeholder={dialog.inputPlaceholder}
            style={{
              width: "100%",
              marginTop: 18,
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid #cbd5e1",
              fontSize: 15,
              fontWeight: 800,
              boxSizing: "border-box"
            }}
          />
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
          {dialog.mode !== "alert" && (
            <button
              onClick={() => closeDialog(false)}
              style={{
                padding: "11px 14px",
                borderRadius: 12,
                border: "1px solid #cbd5e1",
                background: "#fff",
                color: "#475569",
                fontWeight: 900,
                cursor: "pointer"
              }}
            >
              {dialog.cancelText}
            </button>
          )}

          <button
            autoFocus={dialog.mode !== "prompt"}
            onClick={() => closeDialog(true)}
            style={{
              padding: "11px 16px",
              borderRadius: 12,
              border: "none",
              background: palette.button,
              color: "#fff",
              fontWeight: 900,
              cursor: "pointer"
            }}
          >
            {dialog.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AppDialogProvider;
